import {
    Component, EventEmitter, forwardRef, Input,
    OnDestroy, OnInit, Output, ViewChild, AfterViewInit
} from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR, NG_VALIDATORS } from '@angular/forms';
import { Observable, of, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, share } from 'rxjs/operators';

import { Company } from '@data/models/company.model';
import { CompanyService } from '@data/services/company.service';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';

// Import internal
@Component({

    selector: 'syn-company-picker',
    templateUrl: 'company-picker.component.html',
    styleUrls: ['company-picker.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => CompanyPickerComponent),
            multi: true
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => CompanyPickerComponent),
            multi: true
        }

    ]
})
export class CompanyPickerComponent implements OnInit, OnDestroy, ControlValueAccessor, AfterViewInit {

    @Output() companyChange = new EventEmitter<Company>();

    @Input() placeholder = '';
    @Input() emitAndClear = false;
    @Input() disabled = false;
    @Input() includeInactive = true;

    @ViewChild(MatAutocompleteTrigger, { static: true }) trigger;

    companyIdValue: string;
    companyTypeValue: string;
    selectedCompany: Company;
    filteredCompanies: Observable<Company[]>;
    searchTerm: FormControl;

    /** Whether this is required */
    protected _required = false;
    private subscription: Subscription;

    constructor(private companyService: CompanyService) {
        this.searchTerm = new FormControl();
    }

    @Input()
    get companyType() {
        return this.companyTypeValue;
    }

    @Input('value')
    get companyId() {
        return this.companyIdValue;
    }

    get required() {
        return this._required;
    }


    /** Required for FormFieldControl. Whether the chip list is required. */
    @Input()
    set required(value: any) {
        this._required = coerceBooleanProperty(value);
    }

    set companyType(val) {
        switch (val) {
            case 'customer':
            case 'partner':
                this.companyTypeValue = val;
                break;
            default:
                this.companyTypeValue = 'all';
                break;
        }
    }

    set companyId(val) {
        if (val && !this.selectedCompany) {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            this.companyService
                .getCompany(val, { includeRemoved: true })
                .toPromise()
                .then(company => {
                    this.selectedCompany = company;
                    this.searchTerm.setValue(company);
                    this.companyChange.emit(company);
                });
        }
        this.companyIdValue = val;
        this.onChange(val);
        this.onTouched();
    }

    onChange: any = () => {
    };

    onTouched: any = () => {
    };

    validateFn: any = () => {
    };

    ngOnInit() {
        this.filteredCompanies = this.searchTerm.valueChanges.pipe(
            debounceTime(600),
            distinctUntilChanged(), // ignore if same as previous
            switchMap((term) => {   // Be careful, term is the search string or a company object
                if (!term) {
                    return of([]);
                }

                const safeTerm = term instanceof Object && term.name ?
                    term.name :
                    term.replace(/"/g, '');

                const filter = [`name:~"${safeTerm}"`];
                if (this.companyType && this.companyType !== 'all') {
                    filter.push(`isCustomer:${this.companyType === 'customer'}`);
                }
                if (!this.includeInactive) {
                    filter.push('status:"ACTIVE"');
                }

                return this.companyService.findCompanies({ filter: filter.join(' '), limit: 10 });
            }),
            share() // mandatory to prevent duplicate http calls
        );
    }

    ngOnDestroy() {
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
        return;
    }

    ngAfterViewInit() {
        // Set selected company on blur
        this.subscription = this.trigger.panelClosingActions
            .subscribe(e => {
                if (!(e && e.source)) {
                    this.searchTerm.setValue(this.selectedCompany);
                    this.trigger.closePanel();
                }
            });
    }

    public registerOnChange(fn) {
        this.onChange = fn;
    }

    public registerOnTouched(fn) {
        this.onTouched = fn;
    }

    public writeValue(companyId) {
        if (companyId) {
            this.companyId = companyId;
        }
    }

    public onSelectionChange(event: any, company?: Company) {
        // change is fired twice : first with the selected, then the previous one
        if (event.source && event.source.selected) {
            this.selectedCompany = company;
            this.companyId = company ? company.id : '';
            this.companyChange.emit(company);
            if (this.emitAndClear) {
                this.searchTerm.setValue(null);
                this.selectedCompany = null;
                this.companyId = '';
            }
        }
    }

    public displayFn(company: Company) {
        return company ? company.name : '';
    }

    public validate(c: FormControl) {
        return this.validateFn(c);
    }

    // Allows Angular to disable the input.
    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
        if (this.disabled) {
            this.searchTerm.disable();
        } else {
            this.searchTerm.enable();
        }
    }
}

